import {
  CalendarDots,
  CaretUpDown,
  CheckCircle,
  CircleDashed,
  Percent,
  RowsPlusBottom,
  UserList,
} from "@phosphor-icons/react";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import {
  ColumnsVisibilityFilter,
  PaginatedDataTable,
} from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import RestrictedComponent from "common/components/RestrictedComponent";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { formatDate } from "common/helpers/utils";
import type { Campaign } from "common/types";

import {
  CAMPAIGN_OUTREACH_OPTIONS,
  MANAGE_CAMPAIGNS_ACTIONS,
} from "../../../constants";
import { CampaignActionsDropdown } from "../../CampaignActions";
import CampaignStateTag from "../../CampaignStateTag";
import WorkerAccountsTag from "../../WorkerAccountsTag";
import CampaignsActions from "../CampaignsActions";
import CampaignCompletion from "./CampaignCompletion";
import CampaignMetrics from "./CampaignMetrics";

import type { ColumnDef, HeaderContext } from "@tanstack/react-table";
import type { ReactNode } from "react";

interface DesktopTableProps {
  isLoading: boolean;
  campaigns: Campaign[];
  selectedCampaigns: Campaign[];
  setSelectedCampaigns: Dispatch<SetStateAction<Campaign[]>>;
  filtersComponent: ReactNode;
}

export default function DesktopTable({
  isLoading,
  campaigns,
  selectedCampaigns,
  setSelectedCampaigns,
  filtersComponent,
}: DesktopTableProps) {
  const navigate = useNavigate();
  const { permissions } = useSelectedWorkspaceContext();

  function onRowClick(campaign: Campaign) {
    navigate(campaign.id);
  }

  const columns = useMemo(() => {
    const campaignColumns: ColumnDef<Campaign>[] = [
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }: HeaderContext<Campaign, unknown>) => (
          <button
            type="button"
            className="flex items-center"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Name <CaretUpDown size={14} />
          </button>
        ),
        cell: ({
          row: {
            original: { outreach_type, name },
          },
        }) => {
          const { icon: SubtypeIcon } =
            CAMPAIGN_OUTREACH_OPTIONS[outreach_type] ?? {};

          return (
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-y-1">
                {isLoading ? (
                  <Skeleton className="h-5 w-40" />
                ) : (
                  <h5
                    className="line-clamp-1 break-all text-md-bold"
                    title={name}
                  >
                    {name}
                  </h5>
                )}

                <div className="flex items-center gap-1">
                  {isLoading ? (
                    <>
                      <Skeleton className="size-4" />
                      <Skeleton className="h-4 w-40" />
                    </>
                  ) : (
                    <>
                      <SubtypeIcon className="size-4 text-black-600" />
                      <span className="line-clamp-1 break-all text-button-xs text-black-500">
                        {outreach_type === "drip" ? "1st" : "2nd & 3rd+"}{" "}
                        connections
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "owners",
        header: "Accounts",
        cell: ({
          row: {
            original: { owners, uses_connection_note },
          },
        }) => {
          if (isLoading) return <Skeleton className="h-6 w-32 rounded-full" />;

          return (
            <WorkerAccountsTag
              size="md"
              owners={owners}
              isUsingNote={uses_connection_note}
            />
          );
        },
        meta: { icon: UserList, className: "min-w-40 max-w-40" },
      },
      {
        id: "state",
        accessorKey: "state",
        header: "Status",
        cell: ({
          row: {
            original: {
              state,
              owners,
              start_date: startDate,
              uses_connection_note,
            },
          },
        }) => {
          if (isLoading) return <Skeleton className="h-6 w-16" />;

          return (
            <CampaignStateTag
              size="md"
              state={state}
              owners={owners}
              startDate={startDate}
              isUsingNote={uses_connection_note}
            />
          );
        },
        meta: { icon: CircleDashed, className: "min-w-40 max-w-40" },
      },
      {
        id: "type",
        header: "Type",
        cell: ({
          row: {
            original: { outreach_type: outreachType },
          },
        }) => {
          if (isLoading) return <Skeleton className="h-6 w-16" />;
          return outreachType === "drip" ? "Inbound" : "Outbound";
        },
        meta: { icon: RowsPlusBottom, className: "min-w-40 max-w-40" },
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created at",
        cell: ({
          row: {
            original: { created_at: createdAt },
          },
        }) =>
          isLoading ? <Skeleton className="h-6 w-20" /> : formatDate(createdAt),
        meta: { icon: CalendarDots, className: "min-w-40 max-w-40" },
      },
      {
        id: "success",
        header: "Success",
        cell: ({ row: { original } }) => {
          if (original.state === "INIT" || original.state === "CREATED") {
            return;
          }

          if (isLoading) {
            return <Skeleton className="h-[18px] w-16" />;
          }

          return (
            <CampaignMetrics
              id={original.id}
              outreachType={original.outreach_type}
            />
          );
        },
        meta: { icon: CheckCircle, className: "min-w-40 max-w-40" },
      },
      {
        id: "completion",
        header: "Progress",
        cell: ({ row: { original } }) => {
          if (original.state === "INIT" || original.state === "CREATED") {
            return;
          }

          if (isLoading) {
            return (
              <div className="flex items-center gap-x-1">
                <Skeleton className="w-8 h-4" />
                <Skeleton className="h-2 w-24" />
              </div>
            );
          }

          return <CampaignCompletion id={original.id} />;
        },
        meta: { icon: Percent, className: "min-w-40 max-w-40" },
      },
      {
        id: "actions",
        cell: ({ row: { original } }) => {
          if (isLoading)
            return <Skeleton className="inline-flex size-8 rounded-full" />;

          return (
            <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
              <CampaignActionsDropdown campaign={original} />
            </RestrictedComponent>
          );
        },
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ];

    return campaignColumns;
  }, [isLoading]);

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "campaigns",
      selectedRows: selectedCampaigns,
      onRowSelectionChange: permissions.includes("campaigns_full")
        ? setSelectedCampaigns
        : undefined,
    },
    reactTableOptions: { data: campaigns, columns },
    isLoading,
  });

  return (
    <>
      <div className="flex flex-row items-center gap-x-1.5">
        {filtersComponent}
        <ColumnsVisibilityFilter table={table} />
      </div>

      <PaginatedDataTable
        title="Campaigns"
        table={table}
        onRowClick={onRowClick}
        isLoading={isLoading}
        selectionActionsComponent={
          <BulkManageDesktop
            actions={MANAGE_CAMPAIGNS_ACTIONS}
            selection={selectedCampaigns}
            renderActionComponent={({ action, onClose }) => (
              <CampaignsActions
                action={action}
                selectedCampaigns={selectedCampaigns}
                onBack={onClose}
                onSuccess={() => {
                  onClose();
                  setSelectedCampaigns([]);
                }}
              />
            )}
          />
        }
      />
    </>
  );
}
